import React from 'react';
import PropTypes from 'prop-types';
import {
  pillBaseClasses,
  pillVariants,
  pillSizes,
  pillTextClasses,
} from '../../constants/pillConstants';

/**
 * Pill Component
 *
 * A reusable and scalable UI component designed for displaying statuses or event information
 * in various parts of the application, including tables and lists.
 *
 * Props:
 * - `text` (string, required): The content to display inside the Pill.
 * - `variant` (string): Determines the background color of the Pill based on predefined variants.
 * - `size` (string): Specifies the size of the Pill (default, small, large).
 * - `className` (string): Allows additional custom styles to be passed.
 * - `role` (string): Accessibility role, default is "status".
 * - `ariaLabel` (string): Custom ARIA label for screen readers, defaults to the text content.
 */
const Pill = ({
  text,
  variant = 'default',
  size = 'default',
  className = '',
  textClassname = '',
  role = 'status',
  ariaLabel,
  ...props
}) => {
  const pillClass = `${pillBaseClasses} ${pillVariants[variant]} ${pillSizes[size]} ${className}`;
  return (
    <div className={pillClass} role={role} aria-label={ariaLabel || text} {...props}>
      <span className={`${pillTextClasses} ${textClassname}`}>{text}</span>
    </div>
  );
};

Pill.propTypes = {
  text: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(Object.keys(pillVariants)),
  size: PropTypes.oneOf(Object.keys(pillSizes)),
  className: PropTypes.string,
  role: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default Pill;
